import { SelectField } from 'components/inputs/SelectField';
import { endOfDay, format, startOfDay } from 'date-fns';
import { capitalize } from 'lodash';
import { PROJECT_STATUS } from 'pages/Projects/Projects.utils';
import { useTranslation } from 'react-i18next';
import { BACKEND_API_TIMESERIES_REQUEST_DATE_FORMAT } from 'utils/constants';

const baseTsStartGtFilter = {
  filterKey: 'ts_start__gt',
};

const baseTsEndLtFilter = {
  filterKey: 'ts_end__lt',
};

const baseIsActiveProjectFilter = {
  filterKey: 'is_active__is',
};

const conjunctionFilter = {
  filterKey: 'conjunction',
  value: 'or',
};

// Function to dynamically generate status filters
const getStatusFilterMap = () => ({
  [PROJECT_STATUS.UPCOMING]: {
    ...baseTsStartGtFilter,
    value: format(endOfDay(new Date()), BACKEND_API_TIMESERIES_REQUEST_DATE_FORMAT),
  },
  [PROJECT_STATUS.COMPLETED]: {
    ...baseTsEndLtFilter,
    value: format(startOfDay(new Date()), BACKEND_API_TIMESERIES_REQUEST_DATE_FORMAT),
  },
  [PROJECT_STATUS.ONGOING]: {
    ...baseIsActiveProjectFilter,
    value: true,
  },
});

// Create a reverse mapping for resolving filter keys back to statuses
const getFilterKeyStatusMap = () => {
  const statusFilterMap = getStatusFilterMap();
  return Object.entries(statusFilterMap).reduce((acc, [status, filter]) => {
    acc[filter.filterKey] = status;
    return acc;
  }, {});
};

export const ProjectStatusFilter = ({ clearFilter, updateFilters, getFilters }) => {
  const { t } = useTranslation();

  const activeFilters = getFilters() || [];

  const filterKeyStatusMap = getFilterKeyStatusMap();
  const statusFilterMap = getStatusFilterMap();

  const activeStatuses = activeFilters.map((filter) => filterKeyStatusMap[filter.filterKey]).filter(Boolean);

  // Prepare options for the SelectField
  const projectStatusOptions = Object.values(PROJECT_STATUS).map((status) => ({
    value: status,
    label: capitalize(status),
  }));

  const handleStatusChange = (e) => {
    const selectedStatuses = e.target.value;

    const filtersToClear = activeStatuses
      .filter((status) => !selectedStatuses.includes(status))
      .map((status) => statusFilterMap[status].filterKey);

    const filtersToAdd = selectedStatuses
      .filter((status) => !activeStatuses.includes(status))
      .map((status) => statusFilterMap[status]);

    if (selectedStatuses.length === 1) {
      // First selection: clear conjunction filter if it exists
      filtersToClear.push(conjunctionFilter.filterKey);
    } else if (selectedStatuses.length > 1) {
      // Multiple selections: ensure conjunction filter is added
      filtersToAdd.push(conjunctionFilter);
    }

    if (filtersToAdd.length > 0) {
      updateFilters(filtersToAdd);
    }

    if (filtersToClear.length > 0) {
      clearFilter(filtersToClear);
    }
  };

  return (
    <SelectField
      value={activeStatuses}
      onChange={handleStatusChange}
      options={projectStatusOptions}
      multiple
      placeholder={t('projects:overview.filterSection.selectStatus')}
      label={t('projects:overview.filterSection.status')}
    />
  );
};
